import { type Ref, watch } from "vue";

import { AlertDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import { splitIntoLines } from "shared/helpers/string";

import { URLS_MAX_COUNT } from "./shared";

export default function useUrlsLimitEnforcing({
  urlsCount,
  urlsInput,
}: {
  urlsCount: Ref<number>;
  urlsInput: Ref<string>;
}) {
  watch(urlsCount, (newUrlsCount, oldUrlsCount) => {
    if (urlsCount.value > URLS_MAX_COUNT) {
      // eslint-disable-next-line no-param-reassign
      urlsInput.value = removeExceedingUrls(urlsInput.value);

      if (oldUrlsCount === URLS_MAX_COUNT) {
        // in this case removing exceeding urls effectively undone last input change
        // so from user's perspective nothing changed after typing/pasting
        showLimitAlreadyReachedDialog();
      } else {
        showLimitWouldBeExceededDialog(newUrlsCount);
      }
    }
  });

  // return the string passed but only with at most {URLS_MAX_COUNT} first urls left and others removed
  function removeExceedingUrls(urlsText: string): string {
    const urlsTextLines = splitIntoLines(urlsText);

    let urlLinesCount = 0;
    let lineIndex = 0;
    let indexOfFirstUrlLineToRemove = urlsTextLines.length; // default if <= URLS_MAX_COUNT

    // starting from the beginning of user's input, count URL lines until reaching URLS_MAX_COUNT + 1
    // to find the index of the first URL line that does not fit within the limit (to keep possible
    // whitespace lines after the last URL line being within the limit)
    while (
      urlLinesCount < URLS_MAX_COUNT + 1 &&
      lineIndex < urlsTextLines.length
    ) {
      // consider non-only-whitespace line as URL (URL regexp validation is being performed later)

      if (urlsTextLines[lineIndex].match(/\S/)) {
        urlLinesCount += 1;
      }

      if (urlLinesCount === URLS_MAX_COUNT + 1) {
        indexOfFirstUrlLineToRemove = lineIndex;
      }

      lineIndex += 1;
    }

    const linesContainingMaxNumberOfUrls = urlsTextLines.slice(
      0,
      indexOfFirstUrlLineToRemove
    );

    return `${linesContainingMaxNumberOfUrls.join("\n")}\n`;
  }

  const WARNING_DIALOG_COLOR = "ds-orange-5";

  function showLimitAlreadyReachedDialog() {
    AlertDialog(
      getLocaleText(
        "external_items_bulk_add_modal.urls_limit_already_reached_warning"
      ),
      { color: WARNING_DIALOG_COLOR }
    );
  }

  function showLimitWouldBeExceededDialog(actualUrlsCount: number) {
    AlertDialog(
      getLocaleText(
        "external_items_bulk_add_modal.urls_limit_would_be_exceeded_warning",
        {
          actualUrlsCount,
          maxUrlsCount: URLS_MAX_COUNT,
        }
      ),
      { color: WARNING_DIALOG_COLOR }
    );
  }
}
